import React, { FC } from "react";
import Image from "next/image";
import { Button } from "@mui/material";
import { styled } from "@mui/material/styles";
import { BsCheckCircleFill } from "react-icons/bs";
import { TFunction } from "next-i18next";

const PREFIX = "SolsEduCourses";

const classes = {
  card: `${PREFIX}-card`,
  copy: `${PREFIX}-copy`,
  title: `${PREFIX}-title`,
  heading: `${PREFIX}-heading`,
  body: `${PREFIX}-body`,
  button: `${PREFIX}-button`,
  checks: `${PREFIX}-checks`,
  shot: `${PREFIX}-shot`,
};

// The main call to action when there is nothing to book on Skills Hub, closed
// or between rounds: the free career courses that cover the same ground are
// SOLS edu's, so the button goes straight into the course app.
//
// Built from the course app's closing banner (`components/index/HomeCta.tsx`
// in sols_edu_pro_pwa_next_react): copy and a checklist on the left, the app
// on the right. Changed for this page: a white card, the SOLS edu mark beside
// the heading in place of the eyebrow, a brand-yellow button, and its own
// screenshot so it does not repeat the course app's homepage. The closure
// notice above it carries a tinted background, so the two never read as one
// block.
//
// Stacks below `lg`, which is 1280 in this site's theme. The course app stacks
// below its `md` (900); here `md` is 768, and two columns at 768 to 1279 left
// the heading seven lines deep beside the screenshot.
const SOLS_EDU_COURSES = "https://courses.solsedu.com";

const Root = styled("section")(({ theme: Theme }) => ({
  margin: "3.5rem 0 1rem",

  [`& .${classes.card}`]: {
    position: "relative",
    display: "grid",
    gridTemplateColumns: "1.15fr 0.85fr",
    alignItems: "center",
    gap: "2rem",
    overflow: "hidden",
    borderRadius: "15px",
    background: "#FFF",
    boxShadow: "02px 2px 15px 5px rgba(120, 120, 120, 0.05)",
    [Theme.breakpoints.down("lg")]: {
      gridTemplateColumns: "1fr",
      gap: "2rem",
    },
  },

  [`& .${classes.copy}`]: {
    position: "relative",
    padding: "3rem 4rem 3rem 4rem",
    [Theme.breakpoints.down("lg")]: {
      padding: "3rem 3rem 0",
    },
    [Theme.breakpoints.down("md")]: {
      padding: "2rem 1.25rem 0",
    },
  },

  [`& .${classes.title}`]: {
    display: "flex",
    alignItems: "center",
    gap: "1.25rem",
    margin: "0 0 1.25rem",
  },

  [`&& .${classes.heading}`]: {
    margin: 0,
    fontSize: "1.75rem",
    lineHeight: 1.3,
    color: "#333333",
    [Theme.breakpoints.down("lg")]: {
      fontSize: "1.5rem",
    },
  },

  [`&& .${classes.body}`]: {
    margin: "0 0 1.75rem",
    color: "#333333",
    lineHeight: 1.7,
  },

  [`& .${classes.button}`]: {
    background: Theme.palette.primary.main,
    color: Theme.palette.primary.dark,
    fontWeight: 700,
    fontSize: "1rem",
    padding: "0.75rem 1.75rem",
    borderRadius: "0.5rem",
    "&:hover": {
      background: Theme.palette.primary.dark,
      color: "#ffffff",
    },
  },

  [`&& .${classes.checks}`]: {
    display: "flex",
    flexWrap: "wrap",
    gap: "0.75rem 1.5rem",
    listStyle: "none",
    padding: 0,
    margin: "2rem 0 0",
    "& li": {
      display: "flex",
      alignItems: "center",
      gap: "0.5rem",
      padding: 0,
      fontWeight: 700,
      color: "#555555",
    },
    "& svg": {
      flexShrink: 0,
      color: "#EA4535",
      fontSize: "1.1rem",
    },
  },

  // Flush with the card's right edge and the screenshot's own right edge,
  // which is where the desktop window is cut off, so the crop reads as the
  // window running on past the card.
  [`& .${classes.shot}`]: {
    position: "relative",
    alignSelf: "center",
    justifySelf: "end",
    width: "100%",
    maxWidth: "36rem",
    padding: "2rem 0 1.8rem",
    lineHeight: 0,
    [Theme.breakpoints.down("lg")]: {
      padding: "0 0 2rem",
      maxWidth: "min(90%, 36rem)",
    },
  },
}));

const CHECKS = [
  "edu-courses-check-free",
  "edu-courses-check-pace",
  "edu-courses-check-certificate",
];

const SolsEduCourses: FC<Props> = ({ t }) => (
  <Root aria-labelledby="sols-edu-courses">
    <div className={classes.card}>
      <div className={classes.copy}>
        <div className={classes.title}>
          <Image
            src="/assets/images/sols-edu/sols-edu-mark.svg"
            alt=""
            width={72}
            height={72}
          />
          <h2 id="sols-edu-courses" className={classes.heading}>
            {t("edu-courses-h2")}
          </h2>
        </div>
        <p className={classes.body}>{t("edu-courses-p")}</p>
        <Button
          className={classes.button}
          href={SOLS_EDU_COURSES}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t("edu-courses-btn")}
        </Button>
        <ul className={classes.checks}>
          {CHECKS.map((key) => (
            <li key={key}>
              <BsCheckCircleFill aria-hidden />
              {t(key)}
            </li>
          ))}
        </ul>
      </div>

      <div className={classes.shot}>
        <Image
          src="/assets/images/sols-edu/sols-edu-courses-app.png"
          alt={t("edu-courses-alt")}
          width={1080}
          height={904}
          sizes="(max-width: 900px) 90vw, 36rem"
          style={{ width: "100%", height: "auto" }}
        />
      </div>
    </div>
  </Root>
);

interface Props {
  t: TFunction;
}

export default SolsEduCourses;
